import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useWindowSize } from 'react-use'
import { css } from '@emotion/core'
import { isEqual } from 'lodash'

import { Button } from '@/components/atoms/button'
import { AccommodationPaging } from '@/components/molecules/accommodation-paging'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { dashboardPageContainerStyle, tableHeaderThStyle } from '@/constants/layout'
import { OperationLogFiltersType } from '@/models/operation-log'
import { OperationLogRow } from '@/components/organisms/operation-log/row'
import { OperationLogSearch } from '@/components/organisms/operation-log/search'
import { LoadingFull } from '@/components/molecules/loading-full'
import { useAppDispatch, useAppSelector } from '@/features/hooks'
import { fetchOperationLogV2List } from '@/features/operation-log/operation-log-thunks'
import { setOpenedBatchUuid } from '@/features/operation-log/operation-log-slice'
import { hasOperationLogNextPage } from './has-operation-log-next-page'
import { operationLogV2QueryNeedsNormalize, parseOperationLogV2Query, stringifyOperationLogV2Query } from '@/utils/operation-log-url'
import { filterRedundantGenericSalesSummaryRows } from '@/utils/operation-log-sales-display'
import { operationLogListScrollStyle, operationLogTableStyle } from '@/components/organisms/operation-log/table-cell-styles'
import { useScrollToTop } from '@/hooks/use-scroll-to-top'

const limit = 50

/** 操作ログ「新」一覧（GET …/operation-log/v2）。旧画面とは別ルート。 */
export const OperationLogV2Screen: React.FC = () => {
  const windows = useWindowSize()
  const history = useHistory()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const dispatch = useAppDispatch()
  const list = useAppSelector(s => s.operationLog.list)
  const listStatus = useAppSelector(s => s.operationLog.listStatus)
  const page = useAppSelector(s => s.operationLog.page)
  const perPage = useAppSelector(s => s.operationLog.perPage)
  const filters = useAppSelector(s => s.operationLog.filters)
  const listMeta = useAppSelector(s => s.operationLog.listMeta)
  const listLinks = useAppSelector(s => s.operationLog.listLinks)
  const openedBatchUuid = useAppSelector(s => s.operationLog.openedBatchUuid)
  const { scrollRef: listScrollRef, scrollToTop: scrollListToTop } = useScrollToTop()

  const [isOpenDetailedSearch, setIsOpenDetailedSearch] = useState(false)
  const loading = listStatus === 'loading'

  const displayList = useMemo(() => {
    const genericSalesLabels = [t('OperationLog.target.sales_default'), '売上', 'Sales'] as const
    return filterRedundantGenericSalesSummaryRows(list, { genericSalesLabels })
  }, [list, t])

  const hasNextPage = useMemo(
    () => hasOperationLogNextPage(listMeta, listLinks, list.length, perPage),
    [listMeta, listLinks, list.length, perPage],
  )

  useEffect(() => {
    const { filters: qFilters, page: qPage } = parseOperationLogV2Query(location.search)
    if (operationLogV2QueryNeedsNormalize(location.search, qFilters, qPage)) {
      const qs = stringifyOperationLogV2Query(qFilters, qPage)
      history.replace({ pathname: location.pathname, search: qs ? `?${qs}` : '' })
      return
    }
    dispatch(setOpenedBatchUuid(null))
    dispatch(fetchOperationLogV2List({ filters: qFilters, page: qPage, limit }))
      .unwrap()
      .then(() => scrollListToTop())
      .catch(err => errorHandler(err))
  }, [dispatch, errorHandler, history, location.pathname, location.search, scrollListToTop])

  const onChangeSearchFilters = (data: OperationLogFiltersType) => {
    if (!isEqual(data, filters)) {
      dispatch(setOpenedBatchUuid(null))
      const qs = stringifyOperationLogV2Query(data, 1)
      history.replace({ pathname: location.pathname, search: qs ? `?${qs}` : '' })
    }
    setIsOpenDetailedSearch(false)
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage === page) return
      dispatch(setOpenedBatchUuid(null))
      const qs = stringifyOperationLogV2Query(filters, newPage)
      history.replace({ pathname: location.pathname, search: qs ? `?${qs}` : '' })
    },
    [dispatch, filters, history, location.pathname, page],
  )

  return (
    <div css={[dashboardPageContainerStyle, { position: 'relative' }]}>
      <LoadingFull isLoading={loading} />
      <div css={headerSettingOperationLogStyle}>
        <div css={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <div css={headerDetailStyle}>{t('Operation history')}</div>
          <Button
            buttonType={4}
            width={i18n.language === 'en' ? 118 : 90}
            height={32}
            fontSize={12}
            marginLeft={24}
            marginRight={12}
            onClick={() => setIsOpenDetailedSearch(!isOpenDetailedSearch)}
          >
            {t('Detailed search')}
          </Button>
          <Link to="/operation-log/legacy" css={screenNavLinkStyle}>
            {t('OperationLog.navToLegacy')}
          </Link>
        </div>
        <AccommodationPaging page={Number(page)} setPage={handlePageChange} hasNextPage={hasNextPage} limit={perPage} />
      </div>
      <div css={{ display: isOpenDetailedSearch ? 'block' : 'none' }}>
        <OperationLogSearch onChange={onChangeSearchFilters} initialFilters={filters} />
      </div>
      <div ref={listScrollRef} css={[operationLogListScrollStyle, { height: windows.height - 154 }]}>
        {!!displayList.length && (
          <table css={operationLogTableStyle}>
            <thead>
              <tr>
                <th css={[tableHeaderThStyle, { paddingLeft: 28, width: '5%' }]} />
                <th css={[tableHeaderThStyle, { width: '15%' }]}>{t('Date and time of operation')}</th>
                <th css={[tableHeaderThStyle, { width: '10%' }]}>{t('Operation')}</th>
                <th css={[tableHeaderThStyle, { width: '10%' }]}>{t('OperationLog.feature')}</th>
                <th css={[tableHeaderThStyle, { width: '25%' }]}>{t('OperationLog.summary')}</th>
                <th css={[tableHeaderThStyle, { width: '18%' }]}>{t('Account')}</th>
                <th css={[tableHeaderThStyle, { width: '17%', paddingRight: 28 }]}>{t('Staff')}</th>
              </tr>
            </thead>
            <tbody>
              {displayList.map(item => (
                <OperationLogRow
                  key={item.batch_uuid}
                  item={item}
                  isOpen={openedBatchUuid === item.batch_uuid}
                  onToggle={() => dispatch(setOpenedBatchUuid(openedBatchUuid === item.batch_uuid ? null : item.batch_uuid))}
                />
              ))}
            </tbody>
          </table>
        )}
        {!displayList.length && !loading && (
          <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <h1>{t('No data available')}</h1>
          </div>
        )}
      </div>
    </div>
  )
}

const headerSettingOperationLogStyle = css({
  height: '50px',
  padding: '0 32px',
  borderBottom: '1px solid #F2F2F2',
  display: 'flex',
  justifyContent: 'space-between',
})

const headerDetailStyle = css({
  fontSize: '18px',
  fontWeight: 'bold',
  padding: '16px 0',
  color: '#272727',
})

const screenNavLinkStyle = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: 8,
  minHeight: 32,
  padding: '0 16px',
  borderRadius: 24,
  fontSize: 12,
  fontWeight: 'bold',
  backgroundColor: '#fff',
  border: '1px solid #CCCCCC',
  color: '#272727',
  textDecoration: 'none',
  boxSizing: 'border-box',
  '&:hover': {
    opacity: 0.88,
  },
})
